import { HttpClient } from '@angular/common/http';
import { inject, Service } from '@angular/core';
import { EnvironmentService } from '@indutrust/shared-env';
import type { Contact } from '@indutrust/shared-model';
import type { Observable } from 'rxjs';

/**
 * Payload accepted by `POST /companies/{companyId}/create_contact`.
 *
 * The backend wraps the body in a Rails-style `{ contact: {...} }` envelope;
 * that shape stays internal to this service — callers pass the contact data
 * flat.
 */
export type CreateCompanyContactPayload = Pick<
  Contact,
  'first_name' | 'last_name' | 'email' | 'number' | 'type'
>;

/**
 * API client dedicated to creating and attaching a {@link Contact} to a
 * {@link import('@indutrust/shared-model').Company}.
 *
 * Kept in a separate file from `CompanyApiClient` because it hits a custom
 * action endpoint (not a REST resource) and does not fit the `BaseApiClient<T>`
 * shape.
 */
@Service()
export class CompanyContactApiClient {
  private readonly envService = inject(EnvironmentService);
  private readonly http = inject(HttpClient);

  /**
   * Creates a new contact and attaches it to the given company.
   * @param companyId - The id of the company the contact will be attached to.
   * @param contact - The contact payload.
   * @returns An observable emitting the created {@link Contact}.
   */
  create(
    companyId: number,
    contact: CreateCompanyContactPayload
  ): Observable<Contact> {
    return this.http.post<Contact>(
      `${this.envService.getApiUrl()}/companies/${companyId}/create_contact`,
      { contact }
    );
  }
}
